/**
 * Canonical authentication types for provider-agnostic auth.
 *
 * These types define the normalized user information used throughout
 * the application, regardless of which identity provider is in use.
 */

// Re-export SignoutRedirectArgs from oidc-client-ts for logout customization
export type { SignoutRedirectArgs } from 'oidc-client-ts';

/**
 * Normalized user claims after authentication.
 * Provider-agnostic representation used throughout the application.
 */
export interface CanonicalUserClaims {
  /** Provider-agnostic user identifier */
  sub: string;
  /** User's email address */
  email: string;
  /** User's group memberships */
  groups: string[];
  /** Display name (optional) */
  name?: string;
  /** Source provider: 'cognito', 'entra', 'local' */
  provider: string;
}

/**
 * Configuration for mapping provider-specific claims to canonical claims.
 */
export interface ClaimMappingConfig {
  /** Claim containing user ID (e.g., 'sub' for Cognito, 'oid' for Entra) */
  userIdClaim: string;
  /** Claim containing email */
  emailClaim: string;
  /** Claim containing groups (e.g., 'cognito:groups', 'groups') */
  groupsClaim: string;
  /** Claim containing display name */
  nameClaim: string;
}

/**
 * Provider-specific claim mapping configurations.
 */
export const PROVIDER_CLAIM_MAPPINGS: Record<string, ClaimMappingConfig> = {
  cognito: {
    userIdClaim: 'sub',
    emailClaim: 'email',
    groupsClaim: 'cognito:groups',
    nameClaim: 'name',
  },
  entra: {
    userIdClaim: 'oid',
    emailClaim: 'email',
    groupsClaim: 'groups',
    nameClaim: 'name',
  },
  local: {
    userIdClaim: 'sub',
    emailClaim: 'email',
    groupsClaim: 'groups',
    nameClaim: 'name',
  },
};

/**
 * Get the claim mapping configuration for a provider.
 *
 * @param provider - Provider name ('cognito', 'entra', 'local')
 * @returns ClaimMappingConfig for the provider
 */
export function getClaimMapping(provider: string): ClaimMappingConfig {
  return (
    PROVIDER_CLAIM_MAPPINGS[provider] || {
      userIdClaim: 'sub',
      emailClaim: 'email',
      groupsClaim: 'groups',
      nameClaim: 'name',
    }
  );
}

/**
 * OIDC provider configuration.
 */
export interface OidcProviderConfig {
  /** OIDC authority URL (same as issuer) */
  authority: string;
  /** OIDC client ID */
  clientId: string;
  /** OAuth2 redirect URI after authentication */
  redirectUri: string;
  /** Redirect URI after logout */
  postLogoutRedirectUri: string;
  /** OAuth2 scopes to request */
  scope: string;
  /** OAuth2 response type */
  responseType: string;
}

/**
 * Auth provider selection options.
 */
export type AuthProviderType = 'cognito' | 'entra' | 'both' | 'local';
